import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useCurrentWorkflow, useSyncRapidDataFromWorkflow, useFetchPublicRapidData } from "@/store";
import useApi from "@/hooks/useApi";

/**
 * RapidDataInitializer - Handles automatic sync of rapid data when workflow changes
 *
 * This component should be mounted once in your app (e.g., in App.tsx) to ensure
 * rapid data is synced with the current workflow.
 */
export function RapidDataInitializer() {
    const workflow = useCurrentWorkflow();
    const syncRapidDataFromWorkflow = useSyncRapidDataFromWorkflow();
    const fetchPublicRapidData = useFetchPublicRapidData();
    const location = useLocation();
    const api = useApi();
    const { shareableSlug } = useParams<{ shareableSlug: string }>();

    const isPublicRoute = location.pathname.includes("/p/");

    useEffect(() => {
        // Skip if no workflow or no rapidDataId
        if (!workflow?.rapidDataId) {
            syncRapidDataFromWorkflow(null);
            return;
        }

        // For public routes, fetch via API instead of Firebase sync
        if (isPublicRoute) {
            // If the public route is the same as the workflow's public URL, fetch the rapid data
            if (workflow.rapidData?.url === shareableSlug) {
                fetchPublicRapidData(workflow.rapidDataId, api);
            }
        } else {
            // For authenticated routes, sync via Firebase
            syncRapidDataFromWorkflow(workflow.rapidDataId);
        }
    }, [
        workflow,
        isPublicRoute,
        syncRapidDataFromWorkflow,
        fetchPublicRapidData,
        api,
        shareableSlug
    ]);

    // This component doesn't render anything
    return null;
}
